/**
 * Terrain layer.
 *
 * One instanced quad per tile. A 128x128 map is 16384 instances in a single
 * draw call, and a tile that changes (ore being mined out) costs one float
 * write plus a partial buffer upload rather than a geometry rebuild.
 */

import * as THREE from 'three/webgpu';
import { float, positionGeometry, texture, vec3 } from 'three/tsl';
import { QuadLayer, atlasUV, attr } from './instanced.js';
import { TILE_COUNT } from '../game/constants.js';

export class TerrainLayer {
  readonly layer: QuadLayer;
  private width = 0;
  private height = 0;
  private tileTypes: Float32Array = new Float32Array(0);
  private dirty = false;

  constructor(atlas: THREE.Texture) {
    const material = new THREE.MeshBasicNodeMaterial();

    const tilePos = attr('aTilePos', 'vec2');
    const tileType = attr('aTileType', 'float');

    // Quad spans -0.5..0.5, so shift it to cover the tile's own cell.
    material.positionNode = vec3(
      positionGeometry.x.add(tilePos.x).add(0.5),
      positionGeometry.y.sub(tilePos.y).sub(0.5),
      float(0),
    );
    material.colorNode = texture(atlas, atlasUV(float(0), tileType, float(1), float(TILE_COUNT)));
    material.transparent = false;
    material.depthWrite = true;
    material.depthTest = false;

    this.layer = new QuadLayer({
      capacity: 1,
      attributes: { aTilePos: 2, aTileType: 1 },
      material,
      renderOrder: 0,
    });
  }

  get mesh(): THREE.Mesh {
    return this.layer.mesh;
  }

  /** Uploads a whole map. Call once when a match starts. */
  setMap(width: number, height: number, tiles: Uint8Array): void {
    this.width = width;
    this.height = height;
    const count = width * height;
    this.layer.reserve(count);
    this.tileTypes = new Float32Array(count);

    const pos = this.layer.array('aTilePos');
    const type = this.layer.array('aTileType');
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        pos[i * 2] = x;
        pos[i * 2 + 1] = y;
        type[i] = tiles[i];
        this.tileTypes[i] = tiles[i];
      }
    }
    this.layer.commit(count);
  }

  /**
   * Re-reads terrain from the simulation and updates only what changed.
   * Ore depletion is the only thing that mutates terrain, so this is normally
   * a handful of tiles per second.
   */
  syncTiles(tiles: Uint8Array): void {
    const type = this.layer.array('aTileType');
    const n = Math.min(tiles.length, this.tileTypes.length);
    for (let i = 0; i < n; i++) {
      if (this.tileTypes[i] !== tiles[i]) {
        this.tileTypes[i] = tiles[i];
        type[i] = tiles[i];
        this.dirty = true;
      }
    }
    if (this.dirty) {
      this.layer.commit(this.width * this.height);
      this.dirty = false;
    }
  }

  dispose(): void {
    this.layer.dispose();
  }
}
